import { EChartsOption, PieSeriesOption } from 'echarts'
import { isEmpty, zip } from 'lodash-es'

import tailwind from '_/tailwind.config'
import {
  RISK_THRESHOLD_PALETTE,
  STANDARD_PALETTE,
  type IBasePieChartConfigParams
} from '.'
import {
  chopAndFormatNumberWithCommas,
  formatNumberWithCommas,
  type ITooltipCallbackDataParam
} from '../chartUtils'

type DonutChartSectors = Array<[string, number]>

const FALLBACK_COLOR = 'grey'

export const getBaseDonutChartConfig = (
  config: IBasePieChartConfigParams
): EChartsOption => {
  // QRAGraphType.RISK_BY_SIGNIFICANCE_LEVEL
  const isRiskSignificance = config.graphType === 'riskbysignificancelevel'

  // NOTE: Assume there is corresponding value for each category and vice versa
  const series1 = config.series[0].values
  const series2 = config.series[1]?.values

  const cvPair1 = zip(config.categories, series1 ?? []) as DonutChartSectors
  const cvPair2 = zip(config.categories, series2 ?? []) as DonutChartSectors

  const seriesOption: PieSeriesOption[] = []

  // Determines whether the pie chart consists of one sector only. Used for determining spacing between sectors
  const isOneSector = (cvPair: DonutChartSectors): boolean => {
    let numSectors = 0

    for (const sector of cvPair) {
      if (sector[1] > 0) {
        numSectors += 1
      }
    }

    return numSectors === 1
  }

  // #region Series Option Computation
  const pieSeriesOptionBuilder = (
    cvPair: DonutChartSectors,
    seriesCount: number = 1,
    currentSeries: number = 1
  ): PieSeriesOption => {
    const radiusAllocation = [
      [['35%', '70%']],
      [
        ['30%', '50%'],
        ['55%', '75%']
      ]
    ]

    const filteredPairs = cvPair.filter((pair) => pair[1])

    const getColorForCategory = (name: string) => {
      switch (name) {
        case 'Low':
          return RISK_THRESHOLD_PALETTE[0]
        case 'Medium - Low':
          return RISK_THRESHOLD_PALETTE[1]
        case 'Medium':
          return RISK_THRESHOLD_PALETTE[2]
        case 'Medium - High':
          return RISK_THRESHOLD_PALETTE[3]
        case 'High':
          return RISK_THRESHOLD_PALETTE[4]

        default:
          return FALLBACK_COLOR
      }
    }

    if (isRiskSignificance) {
      // Per-slice color for risk significance
      return {
        type: 'pie',
        radius: radiusAllocation[seriesCount - 1][currentSeries - 1],
        center:
          config.center !== null && config.center === true
            ? ['50%', '50%']
            : ['25%', '45%'],
        itemStyle: {
          borderColor: '#fff',
          borderWidth: isOneSector(filteredPairs) ? 0 : 1.5
        },
        label: {
          show: false,
          position: 'center'
        },
        colorBy: 'data',
        data: filteredPairs.map((k) => ({
          name: k[0],
          value: k[1],
          itemStyle: { color: getColorForCategory(k[0]) }
        }))
      }
    } else {
      // Standard palette for all other donut charts
      return {
        type: 'pie',
        radius: radiusAllocation[seriesCount - 1][currentSeries - 1],
        center:
          config.center !== null && config.center === true
            ? ['50%', '50%']
            : ['25%', '45%'],
        itemStyle: {
          borderColor: '#fff',
          borderWidth: isOneSector(filteredPairs) ? 0 : 1.5
        },
        label: {
          show: false,
          position: 'center'
        },
        color: STANDARD_PALETTE,
        data: filteredPairs.map((k) => ({
          name: k[0],
          value: k[1]
        }))
      }
    }
  }

  const totalSeries = isEmpty(series2) ? 1 : 2

  seriesOption.push(pieSeriesOptionBuilder(cvPair1, totalSeries, 1))

  if (totalSeries > 1) {
    seriesOption.push(pieSeriesOptionBuilder(cvPair2, totalSeries, 2))
  }

  // #endregion

  /**
   * NOTE: Ensure that the sum of all values in the valueList is equal to 100%
   * https://en.wikipedia.org/wiki/Largest_remainder_method
   */
  const toPercent = (
    valueList: DonutChartSectors,
    valueIndex: number,
    precision: number = 2
  ): number => {
    if (!valueList[valueIndex]) {
      return 0
    }

    const numbers = valueList.map((v) => v[1])
    const sum = numbers.reduce((acc, val) => acc + val, 0)

    if (sum === 0) {
      return 0
    }

    const digits = Math.pow(10, precision)

    const votesPerQuota = numbers.map(
      (val) => ((Number.isNaN(val) ? 0 : val) / sum) * digits * 100
    )

    const targetSeats = digits * 100
    const seats = votesPerQuota.map((votes) => Math.floor(votes))

    const remainder = votesPerQuota.map(
      (votes, valueIndex) => votes - seats[valueIndex]
    )

    let currentSum = seats.reduce((acc, val) => acc + val, 0)

    while (currentSum < targetSeats) {
      let max = Number.NEGATIVE_INFINITY
      let maxId = 0

      for (let i = 0, len = remainder.length; i < len; ++i) {
        if (remainder[i] > max) {
          max = remainder[i]
          maxId = i
        }
      }

      ++seats[maxId]
      remainder[maxId] = 0
      ++currentSum
    }

    return seats[valueIndex] / digits
  }

  const trimTooltipItems = (
    _: HTMLElement,
    index: number,
    array: Array<HTMLElement>
  ) => {
    if (config.graphType !== 'riskbysignificancelevel') {
      return true
    }

    // return the label and value only
    if (!index || index === 1 || index === array.length - 1) {
      return true
    }

    return false
  }

  // REVIEW: This function adds line breaks to label names after x amount of characters per line.
  //         This is a hacky solution that can be improved.
  //         Example: formatLineWrap('012345678901234567') returns '01234567890123456\n7'
  const formatLineWrap = (label: string): string => {
    const BREAK_POINT = 16
    let recentSpace = 0

    // The upper limit is label.length - 1 (as opposed to label.length) to avoid edge case where label = 'Risk of Large Leak', overflows.
    // Again, this logic could be refactored.
    for (let i = 0; i < label.length - 1; i++) {
      if (label[i] === ' ') {
        recentSpace = i
      }

      if (i > BREAK_POINT) {
        return (
          label.slice(0, recentSpace) +
          '\n' +
          label.slice(recentSpace + 1, label.length)
        )
      }
    }

    return label
  }

  return {
    textStyle: {
      color: tailwind.theme.colors.gray[500]
    },
    legend: {
      type: 'scroll',
      orient: 'vertical',
      top: '5%',
      right: '0',
      itemWidth: 14,
      data: config.categories,
      formatter: (name: string) => {
        const indexCVPair1 = cvPair1.findIndex((cv) => cv[0] === name)
        const indexCVPair2 = cvPair2.findIndex((cv) => cv[0] === name)

        const cv1 = cvPair1[indexCVPair1]
        const cv2 = cvPair2[indexCVPair2]

        if (cv1 == null) {
          return 'undefined'
        }

        if (cv2?.[1] == null) {
          return isRiskSignificance
            ? `{a|${formatLineWrap(cv1[0])}}\n{b|${formatNumberWithCommas(cv1[1], { maximumFractionDigits: 0 })} ft}`
            : `{a|${formatLineWrap(cv1[0])}}\n{b|${toPercent(
                cvPair1,
                indexCVPair1
              )}%}`
        }

        // NOTE: Assume value is a percentage
        return isRiskSignificance
          ? `{a|${formatLineWrap(cv1[0])}}\n{b|${formatNumberWithCommas(cv1[1], { maximumFractionDigits: 0 })} ft} {b|${formatNumberWithCommas(cv2[1], { maximumFractionDigits: 0 })} ft}`
          : `{a|${formatLineWrap(cv1[0])}}\n{b|${toPercent(
              cvPair1,
              indexCVPair1
            )}%} {b|${toPercent(cvPair2, indexCVPair2)}%}`
      },
      textStyle: {
        padding: [0, 0, 0, 8],
        lineHeight: 16,
        color: tailwind.theme.colors.gray[500],
        rich: {
          a: {
            fontWeight: 'bold'
          },
          b: {
            width: isRiskSignificance ? 84 : 48
          }
        }
      }
    },
    tooltip: {
      backgroundColor: tailwind.theme.colors.black,
      /**
       * Create tooltip that looks something like this:
       *  ----------------------
       * | Pipe Length          |
       * | High|Medium|Low Risk |
       * | 1234.56 ft           |
       *  ----------------------
       */
      formatter: (untypedParams): string | HTMLElement[] => {
        // Delayed typing to bypass non fully typed interface mismatch
        const params = untypedParams as ITooltipCallbackDataParam
        const lightGray = tailwind.theme.colors.gray[300]
        const gray = tailwind.theme.colors.gray[500]
        const { white } = tailwind.theme.colors

        const label = document.createElement('div')
        const categoryLabel = document.createElement('div')
        const percentage = document.createElement('div')
        const valueLabel = document.createElement('div')
        const value = document.createElement('div')

        label.textContent = `${[config.series[params.seriesIndex].label]}`
        label.style.color = white
        label.style.marginBottom = '3px'

        categoryLabel.style.color = params.color ?? lightGray
        percentage.style.color = gray
        valueLabel.style.color = lightGray
        value.style.color = gray

        categoryLabel.innerText = params.name
        valueLabel.innerText = config.valueLabel ?? 'Value'

        let formattedTooltipValue: string

        if (isRiskSignificance) {
          formattedTooltipValue = `${formatNumberWithCommas(
            Number(params.value),
            {
              maximumFractionDigits: 0
            }
          )}${config.unitSymbol ?? ''} ft`
        } else {
          formattedTooltipValue = `${chopAndFormatNumberWithCommas(
            Number(params.value)
          )}${config.unitSymbol ?? ''}${isRiskSignificance ? ' ft' : ''}`
        }

        value.innerText = formattedTooltipValue

        percentage.innerText =
          params.percent == null ? value.innerText : `${params.percent}%`

        valueLabel.style.marginTop = '0.5rem'

        return [label, categoryLabel, percentage, valueLabel, value].filter(
          trimTooltipItems
        )
      }
    },
    series: seriesOption
  }
}
